import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  Heading,
  IconButton,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  UnorderedList,
} from '@chakra-ui/react'
import {
  Discount,
  DiscountType,
  IndividualDiscount,
  Offer,
  OfferDiscounts,
} from 'BrokerTsLib/src/dtos'
import { Insurer } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import _ from 'lodash'
import React, { useContext, useEffect, useState } from 'react'
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form'

import { BaScoringCheckbox } from '@/containers/discounts/BAScoringCheckbox'

import ErrorSummary from '../../../../components/ErrorSummary'
import { FormLabel } from '../../../../components/FormLabel'
import { InputGroup } from '../../../../components/InputGroup'
import { Loading } from '../../../../components/Loading'
import { carkulkaOffersContext } from '../../../../context/CarkulkaOffersProvider'
import { useUrl as useDocumentUrl } from '../../../../hooks/useDocuments'
import { useGetInsurersSpecificParams } from '../../../../hooks/useInsuranceParams'
import {
  useGetOfferAvailableDiscounts,
  useSaveOfferDiscounts,
} from '../../../../hooks/useOffer'
import { Info, Refresh } from '../../../../icons'
import { insuranceParamsContext } from '../../../../layouts/Wizard'
import { LocalInputGroup } from '../../../discounts/IndividualDiscountsWrapper'

const DiscountInputGroup = ({ children }: { children: React.ReactNode }) => {
  return (
    // <Box sx={{ display: 'flex', placeItems: 'start', gap: 4, flexWrap: 'wrap' }}>
    <Box
      sx={{
        display: 'grid',
        placeItems: 'start',
        gap: 4,
        gridTemplateColumns: '1fr 1fr',
      }}
    >
      {children}
    </Box>
  )
}

const LabelHelpPopover = ({ content }: { content: React.ReactNode }) => {
  return (
    <Popover placement="bottom-start">
      <PopoverTrigger>
        <IconButton
          variant="ghost"
          icon={<Info />}
          aria-label="Zobrazit nápovědu"
        />
      </PopoverTrigger>

      <PopoverContent>
        <PopoverBody>{content}</PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

const DiscountInput = ({
  children,
  label,
  labelHelpPopover,
  helperText,
  max,
  unit,
}: {
  children: React.ReactNode
  label: string
  labelHelpPopover?: React.ReactNode
  helperText?: string
  max?: number
  unit?: string
}) => {
  return (
    <FormControl>
      <Flex sx={{ gap: 2 }}>
        <FormLabel sx={{ fontSize: 'sm', fontWeight: 'normal' }}>
          {label}
          {max && `(max. ${max}${unit})`}
        </FormLabel>

        {labelHelpPopover && <LabelHelpPopover content={labelHelpPopover} />}
      </Flex>

      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            min: 0,
            max,
            htmlSize: 8,
            inputMode: 'decimal',
            selectAllonFocus: true,
          } as any)
        }
        return child
      })}

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
}

const DiscountInputWrapper = ({
  discount: x,
  index: i,
  offer,
}: {
  discount: Discount
  index: number
  offer: Offer
}) => {
  const { insuranceParamsId, insuranceParams } = useContext(
    insuranceParamsContext,
  )
  const isContracted = !!insuranceParams?.generalParams.contractNumber

  const documentUrl = useDocumentUrl()

  const methods = useFormContext<OfferDiscounts>()
  const {
    control,
    formState: { errors },
  } = methods

  const isKoopVoucher =
    offer.insurerId == Insurer.Kooperativa && x.type == DiscountType.Voucher

  return (
    <>
      <Controller
        name={`discounts.${i}.discount`}
        defaultValue={x.discount}
        control={control}
        render={({ field: { onChange, value } }) => {
          const error = errors?.discounts
            ? errors.discounts[i]?.discount
            : undefined

          return (
            <>
              <DiscountInput
                label={x.name}
                max={x.maxDiscount}
                unit={x.unit}
                labelHelpPopover={
                  isKoopVoucher && (
                    <Box sx={{ display: 'grid', gap: 2 }}>
                      <Heading size="md">Zadání voucheru</Heading>

                      <UnorderedList variant="">
                        <li>
                          Pro zadání voucheru musíte vyžádat povolení od
                          pojišťovny Kooperativa.
                        </li>
                        <li>
                          Zašlete tento
                          <Link
                            color="blue.500"
                            target="_blank"
                            href={`${documentUrl}/GetOffersPdfForKoopVoucher?paramsId=${insuranceParamsId}&insurerOfferId=${offer.offerId}`}
                          >
                            &nbsp;dokument kalkulace&nbsp;
                          </Link>
                          do pojišťovny Kooperativa na svého KAM.
                        </li>
                        <li>Zde Vám přidělí slevovy voucher.</li>
                        <li>
                          Přidělenou Výši slevy a kód voucheru zadejte do
                          formuláře a dejte přepocítat nabídku.
                        </li>
                      </UnorderedList>
                    </Box>
                  )
                }
              >
                <InputGroup
                  isInvalid={!!error}
                  type="number"
                  value={value ?? ''}
                  htmlSize={1}
                  maxLength={2}
                  isDisabled={isContracted}
                  min={x.minDiscount}
                  max={x.maxDiscount}
                  pattern="[0-9]{0,2}"
                  selectAllonFocus
                  sx={{ w: 12 }}
                  onChange={(e) =>
                    onChange(
                      e.currentTarget.value
                        ? parseInt(e.currentTarget.value)
                        : '',
                    )
                  }
                  errors={error}
                />
              </DiscountInput>
            </>
          )
        }}
      />
      {isKoopVoucher && (
        <Controller
          name={`discounts.${i}.voucher`}
          defaultValue={x.voucher}
          control={control}
          render={({ field: { onChange, value } }) => {
            const error = errors?.discounts
              ? errors.discounts[i]?.discount
              : undefined

            return (
              <DiscountInput label="Kód voucheru">
                <InputGroup
                  isInvalid={!!error}
                  isDisabled={isContracted}
                  value={value ?? ''}
                  sx={{ w: 200 }}
                  onChange={onChange}
                  errors={error}
                />
              </DiscountInput>
            )
          }}
        />
      )}
    </>
  )
}

const InsurerDiscountWrapper = ({
  offer,
  specifics = {},
}: {
  offer: Offer
  specifics: IndividualDiscount | any
}) => {
  const { insuranceParams } = useContext(insuranceParamsContext)
  const isContracted = !!insuranceParams?.generalParams.contractNumber

  const { setValue } = useFormContext()

  const show = [
    Insurer.Uniqa,
    Insurer.Csob,
    Insurer.Slavia,
    Insurer.Pillow,
  ].includes(offer.insurerId)

  return (
    <>
      {show && (
        <Box sx={{ display: 'grid', justifyContent: 'start', gap: 4 }}>
          <FormLabel>Slevy pojišťovny</FormLabel>

          {offer.insurerId == Insurer.Csob && (
            <>
              <FormControl>
                <BaScoringCheckbox
                  isContracted={isContracted}
                  name={`individualDiscount.baScoring`}
                />
              </FormControl>
            </>
          )}

          {offer.insurerId == Insurer.Uniqa && (
            <FormControl>
              <Checkbox
                defaultChecked={specifics.youngDriverComplicity}
                value="1"
                isDisabled={isContracted}
                onChange={(e) =>
                  setValue(
                    `individualDiscount.youngDriverComplicity`,
                    e.target.checked,
                  )
                }
              >
                Spoluúčast mladého řidiče
              </Checkbox>
            </FormControl>
          )}

          {offer.insurerId == Insurer.Slavia && (
            <LocalInputGroup
              id="contract-other-slavia"
              name="contract-other-slavia"
              label="Pojistná smlouva u Slavia pojišťovna"
              optional
              placeholder="Číslo pojistné smlouvy"
              defaultValue={specifics.contractNumber ?? ''}
              onChange={(e: any) =>
                setValue(`individualDiscount.contractNumber`, e.target.value)
              }
            />
          )}

          {offer.insurerId == Insurer.Pillow && (
            <FormControl>
              <Checkbox
                defaultChecked={specifics.vehicleGaraged}
                value="1"
                isDisabled={isContracted}
                onChange={(e) =>
                  setValue(
                    `individualDiscount.vehicleGaraged`,
                    e.target.checked,
                  )
                }
              >
                Parkuje v garáži
              </Checkbox>
            </FormControl>
          )}
        </Box>
      )}
    </>
  )
}

export const DiscountsTab = ({
  offer,
  onSavedHandler,
}: {
  offer: Offer
  onSavedHandler: Function
}) => {
  const { recalculateOffers: recalculate, offers } = useContext(
    carkulkaOffersContext,
  )
  const { insuranceParamsId, insuranceParams } = useContext(
    insuranceParamsContext,
  )
  const isContract = !!insuranceParams?.generalParams.contractNumber

  const { offerId } = offer

  const methods = useForm<OfferDiscounts>({
    defaultValues: {
      discounts: [],
      individualDiscount: { insurer: offer.insurerId },
    },
  })
  const {
    handleSubmit,
    setError,
    formState: { errors },
    setValue,
  } = methods

  const { refetch, data, isLoading, isFetching } =
    useGetOfferAvailableDiscounts(offerId, {
      enabled: false,
      onSuccess: ({ data }) => {
        setValue('discounts', data.filter((x) => x.editK2) ?? [])
      },
    })
  useEffect(() => {
    refetch()
  }, [])

  const [individualDiscounts, setIndividualDiscounts] = useState<
    IndividualDiscount | undefined
  >(undefined)
  const {
    refetch: refetchSpecifics,
    data: dataSpecifics,
    isLoading: isLoadingSpecifics,
    isFetching: isFetchingSpecifics,
    isFetched: isFetchedSpecifics,
  } = useGetInsurersSpecificParams(insuranceParamsId!, offer.insurerId, {
    enabled: false,
    onSuccess: ({ data }) => {
      const specifics = data.filter((x) => x.insurer == offer.insurerId) ?? []
      setValue('individualDiscount', specifics[0])
      setIndividualDiscounts(specifics[0])
    },
  })

  useEffect(() => {
    refetchSpecifics()
  }, [])

  const showSpecifics =
    isFetchedSpecifics && !isLoadingSpecifics && !isFetchingSpecifics
  const discounts = data?.data.filter((x) => x.editK2) ?? []
  const { mutateAsync: saveDiscounts } = useSaveOfferDiscounts(offerId)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const submitForm = handleSubmit((data) => {
    setIsSubmitting(true)

    saveDiscounts(data)
      .then(() => {
        onSavedHandler()
        refetchSpecifics()

        // pokud se zmeni individualni slevy, tak je potreba rekalkulovat vsechny nabidky pojistovny (individualni slevy se ukladani per paramsy a ne offer)
        if (!_.isEqual(data.individualDiscount, individualDiscounts)) {
          setIndividualDiscounts(data.individualDiscount)
          const offerIds = offers.reduce(
            (pv: number[], cv) =>
              cv.insurerId == offer.insurerId ? [...pv, cv.offerId] : pv,
            [],
          )
          recalculate(offerIds, true)
        } else {
          recalculate([offerId], true)
        }
      })
      .catch((err) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data
        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  })

  const anyDiscounts = (data?.data?.length ?? 0) > 0

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <Box
          sx={{
            display: 'grid',
            placeContent: 'start',
            placeItems: 'start',
            gap: 4,
          }}
        >
          <Box
            sx={{
              display: { base: 'grid', md: 'flex' },
              placeItems: 'start',
              gap: { base: 4, lg: 16 },
            }}
          >
            <Box sx={{ display: 'grid', placeItems: 'start', gap: 4 }}>
              <FormLabel>Obchodní slevy</FormLabel>

              <DiscountInputGroup>
                {(isLoading || isFetching) && <Loading large={true} />}
                {!isLoading && !isFetching && !anyDiscounts && (
                  <Text>Žádné slevy nejsou k dispozici</Text>
                )}
                {!isLoading &&
                  !isFetching &&
                  anyDiscounts &&
                  discounts.map((x, i) => {
                    return (
                      <DiscountInputWrapper
                        key={x.insuranceCategoryDiscountId}
                        discount={x}
                        index={i}
                        offer={offer}
                      />
                    )
                  })}
              </DiscountInputGroup>
            </Box>

            {isLoadingSpecifics && <Loading />}
            {showSpecifics && (
              <InsurerDiscountWrapper
                offer={offer}
                specifics={dataSpecifics?.data[0]}
              />
            )}
          </Box>

          {(anyDiscounts || showSpecifics) && !isContract && (
            <Button
              variant="outline"
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
              loadingText="Přepočítávám..."
              onClick={submitForm}
              gridColumnStart={1}
            >
              <Refresh color="secondary.500" />
              Uložit a přepočítat
            </Button>
          )}

          <ErrorSummary errors={errors} />
        </Box>
      </form>
    </FormProvider>
  )
}
